import { useEventCallback } from '@mui/material/utils';
import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { htmlParser } from '@work4all/components/lib/input/format-text/TextEditor/utils/html-parser';

import { ITempFileManagerContext } from '@work4all/data/lib/hooks/data-provider/useTempFileManager';
import { useParams } from '@work4all/data/lib/hooks/routing';
import { Semaphore } from '@work4all/data/lib/settings/sempahore';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { EMail } from '@work4all/models/lib/Classes/EMail.entity';
import { EMailSignature } from '@work4all/models/lib/Classes/EMailSignature.entity';
import { EMailTemplate } from '@work4all/models/lib/Classes/EMailTemplate.entity';
import { ProcessedEMailTemplateAttachment } from '@work4all/models/lib/Classes/ProcessedEMailTemplateAttachment.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';
import { useDebouncedCallback } from '@work4all/utils/lib/hooks/use-debounce-callback';

import { settings, useSetting } from '../../../../../../settings';
import { htmlToPlainText } from '../../../../../../utils/htmlToPlainText';
import { useFormContextPlus } from '../../../../form-plus/use-form-context-plus';
import { EMAILMODES } from '../EmailActions';
import { EmailMaskFormValue } from '../types';

import {
  EmailTemplaterContext,
  EmailTemplaterContextProvider,
} from './email-templater-context';
import { UseEmailTemplaterState } from './use-email-templater-state';
import {
  GetProcessedEmailTemplateVariables,
  useGetProcessedEmailTemplate,
} from './use-get-processed-email-template-query';

export interface EmailTemplaterProviderProps {
  loading: boolean;
  state: UseEmailTemplaterState;
  isCreateMode: boolean;
  languageId?: number;
  tfm: ITempFileManagerContext;
  previousEMail: EMail | false;
  emailMode?: string;
  children?: React.ReactNode;
}

export function EmailTemplaterProvider(props: EmailTemplaterProviderProps) {
  const {
    loading,
    isCreateMode,
    state,
    languageId,
    tfm,
    previousEMail,
    emailMode,
  } = props;

  const { t } = useTranslation();
  const dialogs = useDialogs();

  const form = useFormContextPlus<EmailMaskFormValue>();
  const getProcessedEmailTemplate = useGetProcessedEmailTemplate();

  const saveLastUsedSignature = useEventCallback(
    (signature: EMailSignature) => {
      if (signature) {
        const _signature = pickRequiredEmailSignatureProperties(signature);
        if (!isEqual(_signature, lastUsedEmailSignature.value)) {
          lastUsedEmailSignature.set(_signature);
        }
      } else {
        lastUsedEmailSignature.delete();
      }
    }
  );

  const getInitialEmailBody = useCallback(
    (email: EMail | false, mode: string) => {
      if (!email) return '';

      const date = DateTime.fromISO(email.date);

      const body =
        mode === EMAILMODES.sendAgain
          ? email.bodyHtml
          : `<hr data-next-email-start />
<p>
<b>${t('COMMON.FROM')}:</b> ${email.from || t('COMMON.UNKNOWN')}</br>
<b>${t('MASK.SENT')}:</b> ${date.toLocaleString(DateTimeCustom.DATE_TIME)}</br>
<b>${t('MASK.TO')}:</b> ${email.to}</br>
<b>${t('COMMON.SUBJECT')}:</b> ${email.subject}</br>
</p>
<p>
${email.bodyHtml}
</p>
`;

      // Replace any signature markings and textmark data-items.
      return htmlParser.disableTextmarks(body);
    },
    [t]
  );

  const applyTemplate = useEventCallback(async (template: EMailTemplate) => {
    if (state.isDisabled) return;

    await templateSemaphoreRef.current.wait();

    try {
      const body = form.getValues('bodyHtml') || '';

      if (body.trim().length) {
        const confirmed = await dialogs.confirm({
          title: t('ALERTS.CONFIRM'),
          description: t('ALERTS.TEMPLATE_APPLICATION'),
          confirmLabel: t('ALERTS.BTN_OK'),
          cancelLabel: t('ALERTS.BTN_ABORT'),
        });

        if (!confirmed) {
          // If the user did not confirm the changes, do nothing.
          return;
        }
      }

      try {
        state.setLoading(true);

        state.setTemplate(template);

        if (!template) {
          return;
        }

        template =
          template.childs?.find((child) => child.languageCode === languageId) ??
          template;

        if (template.signature) {
          state.setSignature(template.signature);
          saveLastUsedSignature(template.signature);
        }

        const params: GetProcessedEmailTemplateVariables = {
          ...extractEmailTemplateVariables(form.getValues(), state.variables),
        };

        lastAppliedVariablesRef.current = params;

        const templateBody = template.body;
        const signature = template.signature ?? state.signature;
        const templateBodyWithSignature = htmlParser.replaceSignature(
          templateBody,
          signature?.bodyTextmarkenAufgeloestInlineImagesResolved ?? '',
          { includeDivider: false, useEmailSignature: true }
        );

        const bodyParams = { ...params, createTemplateAttachments: true };
        bodyParams.templateContent = htmlParser.toApi(
          templateBodyWithSignature
        );
        if (!bodyParams.templateContent) {
          bodyParams.templateId = template.id;
        }

        const bodyResponse = await getProcessedEmailTemplate(bodyParams);

        const subjectParams = { ...params };
        subjectParams.templateContent = template.subject;

        let subjectResponse: {
          body: string;
          subject: string | null;
          createdAttachementTempfiles: ProcessedEMailTemplateAttachment[];
        };
        if (subjectParams.templateContent) {
          subjectResponse = await getProcessedEmailTemplate(subjectParams);
        } else {
          subjectResponse = {
            body: '',
            subject: '',
            createdAttachementTempfiles: [],
          };
        }

        if (template.attachmentList?.length > 0) {
          const { createdAttachementTempfiles } =
            await getProcessedEmailTemplate({
              templateId: template.id,
              createTemplateAttachments: true,
            });

          const processedTemplateTempfiles = createdAttachementTempfiles.map(
            (tempFile) => ({
              id: tempFile.id,
              fileName: tempFile.name,
              fileInfos: {
                fileSize: tempFile.size,
              },
              __typename: 'template',
            })
          );

          tfm.setTemporaryFileUploads((temporaryFileUploads) => [
            ...temporaryFileUploads.filter(
              (file) => file?.__typename !== 'template'
            ),
            ...processedTemplateTempfiles,
          ]);
        }

        const body = htmlParser.cleanUpTextmarks(bodyResponse.body);

        form.setValue(
          'bodyHtml',
          body + getInitialEmailBody(previousEMail, emailMode),
          { shouldDirty: true }
        );
        form.setValue('subject', htmlToPlainText(subjectResponse.body), {
          shouldDirty: true,
        });
      } finally {
        state.setLoading(false);
      }
    } finally {
      templateSemaphoreRef.current.release();
    }
  });

  const isInitializedRef = useRef(false);

  const lastAppliedVariablesRef =
    useRef<GetProcessedEmailTemplateVariables>(null);

  // Semaphore to ensure applyTemplate runs in sync with refreshTemplate.
  const templateSemaphoreRef = useRef(new Semaphore(1));

  const refreshTemplate = useDebouncedCallback(
    useEventCallback(async () => {
      if (state.isDisabled) return;
      if (!state.template) return;

      const body = form.getValues('bodyHtml') || '';

      if (!body) return;

      await templateSemaphoreRef.current.wait();

      try {
        try {
          state.setLoading(true);

          const params: GetProcessedEmailTemplateVariables = {
            ...extractEmailTemplateVariables(form.getValues(), state.variables),
          };

          if (isEqual(lastAppliedVariablesRef.current, params)) {
            return;
          }

          lastAppliedVariablesRef.current = params;

          const { newEmailContent, previousEmailContent } =
            separateEmailContent(body);

          const bodyParams = { ...params };
          bodyParams.templateContent = htmlParser.toApi(newEmailContent);

          const response = await getProcessedEmailTemplate(bodyParams);
          const newBody = response.body + previousEmailContent;

          form.setValue('bodyHtml', newBody, { shouldDirty: true });
        } finally {
          state.setLoading(false);
        }
      } finally {
        templateSemaphoreRef.current.release();
      }
    }),
    200
  );

  // Refresh template when the form values change
  useEffect(() => {
    if (state.isDisabled || !state.template) {
      return;
    }

    const sub = form.watch(() => {
      refreshTemplate();
    });

    return () => {
      sub.unsubscribe();
    };
  }, [form, refreshTemplate, state.isDisabled, state.template]);

  const lastUsedEmailSignature = useSetting(settings.lastUsedEmailSignature());

  const applySignature = useEventCallback(
    async (
      signature: EMailSignature,
      body: string = form.getValues('bodyHtml') || ''
    ) => {
      if (state.isDisabled) return;

      state.setSignature(signature);
      saveLastUsedSignature(signature);

      body = htmlParser.replaceSignature(
        body,
        signature?.bodyTextmarkenAufgeloestInlineImagesResolved || ' ',
        { includeDivider: false, useEmailSignature: true }
      );

      // If body is empty, add an empty line before adding signature
      const isEmptyBody =
        !body || body.trim() === '' || body.trim() === '<p></p>';
      if (isEmptyBody) {
        body = '<br>';
      }

      body = htmlParser.replaceSignature(
        body,
        signature?.bodyTextmarkenAufgeloestInlineImagesResolved || ' ',
        { includeDivider: false, useEmailSignature: true }
      );

      try {
        state.setLoading(true);

        const params: GetProcessedEmailTemplateVariables = {
          ...extractEmailTemplateVariables(form.getValues(), state.variables),
        };

        lastAppliedVariablesRef.current = params;

        const { newEmailContent, previousEmailContent } =
          separateEmailContent(body);

        const bodyParams = { ...params };
        bodyParams.templateContent = htmlParser.toApi(newEmailContent);

        const response = await getProcessedEmailTemplate(bodyParams);
        const newBody = response.body + previousEmailContent;

        form.setValue('bodyHtml', newBody, { shouldDirty: true });
      } finally {
        state.setLoading(false);
      }
    }
  );

  // Apply the initial template, signature and/or copy the previous email body.
  useEffect(() => {
    if (isInitializedRef.current) return;
    // Wait for the all dependencies to be loaded.
    if (loading || state.isLoading || previousEMail == null) return;

    isInitializedRef.current = true;

    if (!isCreateMode) return;

    if (state.isDisabled || !state.template) {
      const body = getInitialEmailBody(previousEMail, emailMode);

      // Apply signature if available
      if (!state.isDisabled && state.signature) {
        applySignature(state.signature, body);
      } else {
        // Workaround to set the value after a form is reset.
        setTimeout(() => {
          form.setValue('bodyHtml', body, { shouldDirty: true });
          state.setLoading(false);
        });
      }
    } else {
      // When using a template, the signature is already applied in applyTemplate
      applyTemplate(state.template);
    }
  }, [
    applySignature,
    applyTemplate,
    emailMode,
    form,
    getInitialEmailBody,
    isCreateMode,
    loading,
    previousEMail,
    state,
  ]);

  const { '*': wildcard } = useParams();
  const isGeneralEmailSource = wildcard.split('/').includes('eMail');

  const context = useMemo<EmailTemplaterContext>(() => {
    return {
      disabled: state.isDisabled,
      template: state.template,
      signature: state.signature,
      isLoading: state.isLoading,
      applyTemplate: applyTemplate,
      applySignature: applySignature,
      isGeneralEmailSource: isGeneralEmailSource,
      initialTemplate: state.initialTemplate,
    };
  }, [
    state.isDisabled,
    state.template,
    state.signature,
    state.isLoading,
    state.initialTemplate,
    applyTemplate,
    applySignature,
    isGeneralEmailSource,
  ]);

  return (
    <EmailTemplaterContextProvider value={context} children={props.children} />
  );
}

const CONST_ARRAY = [];

function extractEmailTemplateVariables(
  values: EmailMaskFormValue,
  additionalTemplateVariables: Record<string, unknown> = {}
): Omit<
  GetProcessedEmailTemplateVariables,
  'userId' | 'templateId' | 'templateContent'
> {
  const businessPartner = values.businessPartner?.data;
  const customer = isCustomer(businessPartner) ? businessPartner : null;
  const supplier = isSupplier(businessPartner) ? businessPartner : null;
  const project = values.project ?? null;
  const contact = values.contact;
  const isPrivatePartner =
    (supplier || customer) && businessPartner?.isPrivateCustomer;
  const emailContactIds =
    values.toMailContactsInstance
      ?.map((value) => value.id)
      .filter(Boolean)
      .map((id) =>
        id.startsWith('Email_') && isPrivatePartner
          ? `${businessPartner.__typename}_${businessPartner.id}`
          : id
      )
      .filter((id) => {
        //we dont send generic email addresses to the template, they can be used anyhow
        return !id.startsWith('Email_');
      }) || CONST_ARRAY;

  return {
    customerId: customer?.id,
    supplierId: supplier?.id,
    projectId: project?.id,
    emailContactIds,
    ...(contact != null && (customer != null || supplier != null)
      ? {
          contactId: contact.id,
          businessPartnerType:
            customer != null ? SdObjType.KUNDE : SdObjType.LIEFERANT,
        }
      : null),
    ...additionalTemplateVariables,
  };
}

function isCustomer(
  businessPartner: Customer | Supplier
): businessPartner is Customer {
  return businessPartner?.__typename === 'Kunde';
}

function isSupplier(
  businessPartner: Customer | Supplier
): businessPartner is Supplier {
  return businessPartner?.__typename === 'Lieferant';
}

function pickRequiredEmailSignatureProperties(
  signature: EMailSignature
): EMailSignature {
  const { id, name, __typename } = signature;
  return { id, name, __typename };
}

/**
 * Separates the email body into new email content and previous email content
 * based on the data-next-email-start marker.
 */
function separateEmailContent(body: string): {
  newEmailContent: string;
  previousEmailContent: string;
} {
  const markerIndex = body.indexOf('<hr data-next-email-start');

  if (markerIndex === -1) {
    //  If no previous email content - return everything as new email content
    return {
      newEmailContent: body,
      previousEmailContent: '',
    };
  }

  const newEmailContent = body.substring(0, markerIndex);
  const previousEmailContent = body.substring(markerIndex);

  return {
    newEmailContent,
    previousEmailContent,
  };
}
